import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { quickstartAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  CheckCircle2, Circle, ArrowRight, Sparkles, X, Lock,
} from 'lucide-react';

/**
 * Onboarding / setup checklist. Lives on the dashboard until every step
 * is done OR the user dismisses it.
 *
 * Each step is live-derived on the backend (see /me/quickstart):
 *   Paid users:
 *     0. Complete profile & branding
 *     1. Connect a data source (Justimmo or Willhaben)
 *     2. Render first video
 *     3. Connect social accounts
 *     4. Enable Auto-Post
 *   Free users get a short guided checklist (the "wow" render + profile
 *   personalization) — the stickier they make it, the better the videos.
 *
 * Steps that aren't actionable yet (e.g. "Auto-Post" before any
 * source is connected) get a `blocked: true` flag. The UI still
 * lists them but routes the user to the prerequisite first instead
 * of dead-ending them.
 *
 * Designed to feel like progress, not a chore: green checkmarks +
 * percentage progress bar + dismissable by the user. No timed
 * popups, no spotlights, no modal overlays. It's a friendly card
 * the user can ignore if they prefer to explore on their own.
 */
export default function QuickstartChecklist() {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    quickstartAPI.get()
      .then(r => setData(r.data))
      .catch(() => setData({ show: false }));
  }, []);

  const handleDismiss = async () => {
    setData(prev => ({ ...prev, dismissed_at: 'now' })); // optimistic hide
    quickstartAPI.dismiss().catch(() => { /* best-effort */ });
  };

  if (!data || !data.show || data.dismissed_at) return null;

  const items = data.items || [];
  const completed = data.completed || 0;
  const total = data.total || items.length;
  const allDone = completed === total && total > 0;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <Card className="glass border-[#1f4d3a]/40 bg-gradient-to-br from-[#1f4d3a]/5 to-transparent mb-6">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-[#23211a] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#1f4d3a]" />
              {allDone
                ? (t('quickstart.allDoneTitle') || 'Du bist startklar!')
                : (t('quickstart.title') || 'Deine ersten Schritte')}
            </h3>
            <p className="text-xs sm:text-sm text-[#6f6a5c] mt-0.5">
              {allDone
                ? (t('quickstart.allDoneSubtitle') || 'Alle Setup-Schritte erledigt. Lehn dich zurück. Der Rest läuft automatisch.')
                : data.tier === 'free'
                  ? (t('quickstart.subtitleFree') || 'Dein erstes Video und dein Branding — so holst du das Meiste aus Real Content heraus.')
                  : (t('quickstart.subtitle') || 'In 4 Schritten zum vollautomatischen Posting. Du kannst jederzeit unterbrechen.')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1.5 rounded hover:bg-[#efe9db] text-[#6f6a5c] hover:text-[#23211a] transition shrink-0"
            aria-label={t('common.close') || 'Schließen'}
            data-testid="quickstart-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5 text-xs text-[#6f6a5c]">
            <span>{completed} / {total} {t('quickstart.completed') || 'erledigt'}</span>
            <span className="text-[#1f4d3a] font-semibold">{pct}%</span>
          </div>
          <div className="h-1.5 bg-[#e9e2d2] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${allDone ? 'bg-[#1e7a4f]' : 'bg-[#1f4d3a]'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-2">
          {items.map((it, i) => {
            const label = lang === 'en' ? it.label_en : it.label_de;
            const hint = lang === 'en' ? it.hint_en : it.hint_de;
            const isBlocked = it.blocked && !it.done;
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => navigate(it.link)}
                disabled={isBlocked}
                className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition group ${
                  it.done
                    ? 'border-[#1e7a4f]/40 bg-[#1e7a4f]/5'
                    : isBlocked
                      ? 'border-[#e3dccb] bg-[#f2ecdf] opacity-60 cursor-not-allowed'
                      : 'border-[#e3dccb] hover:border-[#1f4d3a] hover:bg-[#f0f3ec] cursor-pointer'
                }`}
                data-testid={`quickstart-step-${it.id}`}
              >
                <div className="shrink-0 mt-0.5">
                  {it.done ? (
                    <CheckCircle2 className="w-5 h-5 text-[#1e7a4f]" />
                  ) : isBlocked ? (
                    <Lock className="w-5 h-5 text-[#8a8474]" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-[#c9c1ac] group-hover:border-[#1f4d3a] flex items-center justify-center text-[10px] font-bold text-[#8a8474] group-hover:text-[#1f4d3a] transition">
                      {i + 1}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    it.done ? 'text-[#1e7a4f] line-through opacity-80' : 'text-[#23211a]'
                  }`}>
                    {label}
                  </p>
                  {hint && !it.done && (
                    <p className="text-xs text-[#6f6a5c] mt-0.5">{hint}</p>
                  )}
                </div>
                {!it.done && !isBlocked && (
                  <ArrowRight className="w-4 h-4 text-[#8a8474] group-hover:text-[#1f4d3a] shrink-0 mt-1 transition" />
                )}
              </button>
            );
          })}
        </div>

        {allDone && (
          <Button
            onClick={handleDismiss}
            className="mt-5 w-full bg-[#1e7a4f] hover:bg-[#155c3b] text-white"
            data-testid="quickstart-finish"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {t('quickstart.finishBtn') || 'Großartig. Los geht\'s'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
